import { useState, useEffect, useCallback } from 'react';
import { toast } from 'sonner';
import { motion } from 'framer-motion';
import {
  Thermometer, Plus, Trash2, Pencil, Download, FileText,
  FileSpreadsheet, Loader2, Upload, CheckCircle2, XCircle, Save, Star,
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { haccpApi, pdvApi } from '../lib/api';
import api from '../lib/api';
import { useAuth } from '../contexts/AuthContext';
import { useCurrentPdv } from '../contexts/CurrentPdvContext';
import HaccpConsultantHeader from '../components/HaccpConsultantHeader';

const EDITOR_ROLES = ['super_admin', 'admin', 'amministratore', 'rspp'];

const computeConforme = (temp, pointMin, pointMax) => {
  if (temp === '' || temp === null || temp === undefined) return null;
  const t = Number(temp);
  if (Number.isNaN(t)) return null;
  if (pointMin != null && t < pointMin) return false;
  if (pointMax != null && t > pointMax) return false;
  return true;
};

const emptyReading = (point) => ({
  data: new Date().toISOString().slice(0, 10),
  ora: '08:00',
  punto_rilevazione: point?.name || '',
  temperatura: '',
  temperatura_attesa: point ? `${point.expected_min ?? ''}/${point.expected_max ?? ''} ${point.unit || '°C'}`.replace('/ ', '').trim() : '',
  conforme: true,
  azione_correttiva: '',
  operatore: '',
});

const emptyForm = () => ({
  pdv_id: '',
  titolo: '',
  periodo_dal: new Date().toISOString().slice(0, 10),
  periodo_al: new Date().toISOString().slice(0, 10),
  punti_rilevazione_csv: 'Frigo banco vetrina; Freezer; Vetrina espositiva',
  letture: [emptyReading()],
  note: '',
  template_id: '',
});

export default function HaccpTemperaturePage() {
  const { user } = useAuth();
  const { currentPdvId } = useCurrentPdv();
  const canEdit = EDITOR_ROLES.includes(user?.role);

  const [tab, setTab] = useState('records');
  const [pdvList, setPdvList] = useState([]);
  const [records, setRecords] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [preferredId, setPreferredId] = useState(null);
  const [haccpPoints, setHaccpPoints] = useState([]);  // Per la UL selezionata
  const [loading, setLoading] = useState(true);

  const [dialog, setDialog] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(emptyForm());
  const [saving, setSaving] = useState(false);

  // Template upload
  const [uploadDialog, setUploadDialog] = useState(false);
  const [tplFile, setTplFile] = useState(null);
  const [tplName, setTplName] = useState('');
  const [tplDefault, setTplDefault] = useState(true);
  const [uploadingTpl, setUploadingTpl] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [p, r, t, prefs] = await Promise.all([
        pdvApi.getAll(),
        haccpApi.listRecords({ kind: 'temperature' }),
        haccpApi.listTemplates('temperature'),
        haccpApi.getPreferences().catch(() => ({ data: {} })),
      ]);
      setPdvList(p.data);
      setRecords(r.data);
      setTemplates(t.data);
      setPreferredId(prefs?.data?.temperature || null);
    } catch (e) {
      toast.error('Errore caricamento');
    } finally {
      setLoading(false);
    }
  }, []);
  useEffect(() => { load(); }, [load]);

  // Quando cambia la UL nel form: carica i suoi punti di rilevazione
  useEffect(() => {
    if (!form.pdv_id) { setHaccpPoints([]); return; }
    let cancel = false;
    (async () => {
      try {
        const res = await api.get(`/pdv/${form.pdv_id}/haccp-points`);
        if (!cancel) setHaccpPoints(res.data || []);
      } catch (e) { if (!cancel) setHaccpPoints([]); }
    })();
    return () => { cancel = true; };
  }, [form.pdv_id]);

  const seedDefaultPoints = async () => {
    if (!form.pdv_id) return;
    try {
      const res = await api.post(`/pdv/${form.pdv_id}/haccp-points/seed-defaults`);
      setHaccpPoints(res.data || []);
      toast.success(`Caricati ${res.data.length} punti di rilevazione standard`);
    } catch (e) { toast.error('Errore'); }
  };

  const autoPopulateFromPoints = () => {
    if (haccpPoints.length === 0) { toast.error('Nessun punto definito per questa UL. Usa "Carica standard"'); return; }
    const today = new Date().toISOString().slice(0, 10);
    const letture = haccpPoints.map(p => emptyReading(p));
    letture.forEach(r => { r.data = today; r.ora = '08:00'; });
    setForm(p => ({
      ...p,
      punti_rilevazione_csv: haccpPoints.map(x => x.name).join('; '),
      letture,
    }));
    toast.success(`Generate ${letture.length} righe da auto-compilare`);
  };

  const openCreate = () => {
    setEditingId(null);
    const next = emptyForm();
    if (currentPdvId && pdvList.some(p => p.id === currentPdvId)) {
      next.pdv_id = currentPdvId;
    } else if (pdvList.length === 1) {
      next.pdv_id = pdvList[0].id;
    }
    // 1) preferito utente, 2) default globale, 3) primo
    if (preferredId && templates.find(t => t.id === preferredId)) {
      next.template_id = preferredId;
    } else if (templates.length > 0) {
      const def = templates.find(t => t.is_default) || templates[0];
      next.template_id = def.id;
    }
    setForm(next);
    setDialog(true);
  };

  const handleSetPreferred = async (templateId) => {
    try {
      const newId = preferredId === templateId ? null : templateId;
      await haccpApi.setPreference('temperature', newId);
      setPreferredId(newId);
      toast.success(newId ? 'Impostato come preferito' : 'Preferenza rimossa');
    } catch (e) {
      toast.error('Errore');
    }
  };

  const openEdit = async (rec) => {
    try {
      const full = await haccpApi.getRecord(rec.id);
      const d = full.data;
      setForm({
        pdv_id: d.pdv_id || '',
        titolo: d.titolo || '',
        periodo_dal: d.periodo_dal || '',
        periodo_al: d.periodo_al || '',
        punti_rilevazione_csv: (d.punti_rilevazione || []).join('; '),
        letture: (d.letture || []).map(r => ({ ...emptyReading(), ...r })),
        note: d.note || '',
        template_id: d.template_id || '',
      });
      setEditingId(rec.id);
      setDialog(true);
    } catch (e) {
      toast.error('Errore apertura record');
    }
  };

  const handleSave = async () => {
    if (!form.pdv_id) { toast.error('Seleziona UL'); return; }
    if ((form.letture || []).length === 0) { toast.error('Aggiungi almeno una rilevazione'); return; }
    setSaving(true);
    try {
      const punti = (form.punti_rilevazione_csv || '')
        .split(/[;,]+/).map(s => s.trim()).filter(Boolean);
      const letture = form.letture.map((r) => ({
        ...r,
        temperatura: r.temperatura === '' || r.temperatura === null ? null : Number(r.temperatura),
      }));
      const payload = {
        kind: 'temperature',
        pdv_id: form.pdv_id,
        titolo: form.titolo || null,
        periodo_dal: form.periodo_dal || null,
        periodo_al: form.periodo_al || null,
        punti_rilevazione: punti,
        letture,
        note: form.note || null,
        template_id: form.template_id || null,
      };
      if (editingId) {
        await haccpApi.updateRecord(editingId, payload);
        toast.success('Scheda aggiornata');
      } else {
        await haccpApi.createRecord(payload);
        toast.success('Scheda creata');
      }
      setDialog(false);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Errore salvataggio');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Eliminare questa scheda?')) return;
    try {
      await haccpApi.deleteRecord(id);
      toast.success('Eliminata');
      load();
    } catch (e) {
      toast.error('Errore');
    }
  };

  const handleUploadTemplate = async () => {
    if (!tplFile || !tplName) { toast.error('Compila nome e file .docx'); return; }
    setUploadingTpl(true);
    try {
      await haccpApi.uploadTemplate(tplFile, tplName, 'temperature', tplDefault);
      toast.success('Template caricato');
      setUploadDialog(false);
      setTplFile(null); setTplName(''); setTplDefault(true);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Errore upload');
    } finally {
      setUploadingTpl(false);
    }
  };

  const handleDeleteTemplate = async (id) => {
    if (!window.confirm('Eliminare questo template?')) return;
    try {
      await haccpApi.deleteTemplate(id);
      toast.success('Template eliminato');
      load();
    } catch (e) {
      toast.error('Errore');
    }
  };

  const addReading = () => setForm((p) => ({ ...p, letture: [...p.letture, emptyReading()] }));
  const removeReading = (i) => setForm((p) => ({ ...p, letture: p.letture.filter((_, idx) => idx !== i) }));
  const setReading = (i, patch) => setForm((p) => {
    const next = [...p.letture];
    next[i] = { ...next[i], ...patch };
    // Auto-conformità: se è cambiata la temperatura E il punto è nei haccpPoints
    // calcola conforme automaticamente (a meno che non sia override manuale).
    if ('temperatura' in patch) {
      const point = haccpPoints.find(pt => pt.name === next[i].punto_rilevazione);
      if (point) {
        const auto = computeConforme(patch.temperatura, point.expected_min, point.expected_max);
        if (auto !== null) next[i].conforme = auto;
      }
    }
    if ('punto_rilevazione' in patch) {
      const point = haccpPoints.find(pt => pt.name === patch.punto_rilevazione);
      if (point) {
        next[i].temperatura_attesa = `${point.expected_min ?? ''}/${point.expected_max ?? ''} ${point.unit || '°C'}`.replace('/ ', '').trim();
        const auto = computeConforme(next[i].temperatura, point.expected_min, point.expected_max);
        if (auto !== null) next[i].conforme = auto;
      }
    }
    return { ...p, letture: next };
  });

  const pdvName = (id) => pdvList.find(p => p.id === id)?.name || '—';
  const punti = (form.punti_rilevazione_csv || '').split(/[;,]+/).map(s => s.trim()).filter(Boolean);

  return (
    <div className="p-6 space-y-6" data-testid="haccp-temperature-page">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold flex items-center gap-2">
            <Thermometer className="w-7 h-7 text-orange-500" />
            HACCP — Schede Controllo Temperature
          </h1>
          <p className="text-gray-600 mt-1">
            Registra le temperature di frigoriferi, congelatori e vetrine espositive. Output: <strong>DOCX</strong> compilato sul template, <strong>PDF</strong> stampabile e <strong>Excel</strong>.
          </p>
        </div>
        {canEdit && (
          <Button onClick={openCreate} data-testid="new-temperature-btn">
            <Plus className="w-4 h-4 mr-1" /> Nuova Scheda
          </Button>
        )}
      </div>

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList>
          <TabsTrigger value="records" data-testid="tab-records">Schede Compilate</TabsTrigger>
          <TabsTrigger value="templates" data-testid="tab-templates">Template Word</TabsTrigger>
        </TabsList>

        <TabsContent value="records">
          {loading ? (
            <div className="text-center py-12"><Loader2 className="w-6 h-6 animate-spin mx-auto" /></div>
          ) : records.length === 0 ? (
            <Card><CardContent className="py-12 text-center text-gray-500">
              Nessuna scheda. {canEdit && 'Crea la prima cliccando "Nuova Scheda".'}
            </CardContent></Card>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {records.map((r) => (
                <motion.div key={r.id} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}>
                  <Card className="hover:shadow-lg transition-shadow">
                    <CardHeader>
                      <CardTitle className="text-base">{r.titolo || `Scheda ${pdvName(r.pdv_id)}`}</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <Badge variant="outline" className="mb-2">{pdvName(r.pdv_id)}</Badge>
                      <div className="text-sm text-gray-600 space-y-1">
                        <div>📅 {r.periodo_dal} → {r.periodo_al}</div>
                        <div>🌡️ {(r.letture || []).length} rilevazioni</div>
                        <div>📍 {(r.punti_rilevazione || []).slice(0, 2).join(', ')}{(r.punti_rilevazione || []).length > 2 ? '…' : ''}</div>
                      </div>
                      <div className="flex flex-wrap gap-2 mt-4">
                        <Button size="sm" variant="outline" onClick={() => window.open(haccpApi.docxUrl(r.id), '_blank')} data-testid={`docx-${r.id}`}>
                          <FileText className="w-4 h-4 mr-1" /> DOCX
                        </Button>
                        <Button size="sm" variant="outline" onClick={() => window.open(haccpApi.pdfUrl(r.id), '_blank')} data-testid={`pdf-${r.id}`}>
                          <Download className="w-4 h-4 mr-1" /> PDF
                        </Button>
                        <Button size="sm" variant="outline" onClick={() => window.open(haccpApi.excelUrl(r.id), '_blank')} data-testid={`xlsx-${r.id}`}>
                          <FileSpreadsheet className="w-4 h-4 mr-1" /> Excel
                        </Button>
                        {canEdit && (
                          <>
                            <Button size="sm" variant="outline" onClick={() => openEdit(r)}>
                              <Pencil className="w-4 h-4" />
                            </Button>
                            <Button size="sm" variant="outline" onClick={() => handleDelete(r.id)}>
                              <Trash2 className="w-4 h-4 text-red-500" />
                            </Button>
                          </>
                        )}
                      </div>
                    </CardContent>
                  </Card>
                </motion.div>
              ))}
            </div>
          )}
        </TabsContent>

        <TabsContent value="templates">
          <div className="flex justify-between items-center mb-4">
            <p className="text-sm text-gray-600">
              I template <code className="bg-muted px-1 rounded">.docx</code> sono il modello di stampa: usa placeholder come <code>«Nome_Azienda»</code>, <code>«Periodo_dal»</code>, <code>«Periodo_al»</code>, <code>«Consulente_HACCP»</code>, <code>«Luogo_Data_Firma»</code>, <code>«Firma_Consulente»</code>. Per la tabella delle rilevazioni: inserisci una tabella con <code>«TEMP_ROW»</code> nella seconda riga e i placeholder colonna: <code>«data»</code>, <code>«ora»</code>, <code>«punto_rilevazione»</code>, <code>«temperatura»</code>, <code>«temperatura_attesa»</code>, <code>«conforme»</code>, <code>«azione_correttiva»</code>, <code>«operatore»</code>.
            </p>
            {canEdit && (
              <Button onClick={() => setUploadDialog(true)} data-testid="upload-template-btn">
                <Upload className="w-4 h-4 mr-1" /> Carica Template
              </Button>
            )}
          </div>

          {templates.length === 0 ? (
            <Card><CardContent className="py-12 text-center text-gray-500">
              Nessun template caricato.
            </CardContent></Card>
          ) : (
            <div className="space-y-3">
              {templates.map((t) => (
                <Card key={t.id}>
                  <CardContent className="py-4 flex items-center justify-between">
                    <div>
                      <div className="font-semibold flex items-center gap-2">
                        {t.name}
                        {t.is_default && <Badge>Default</Badge>}
                        {preferredId === t.id && <Badge className="bg-amber-100 text-amber-700 border-amber-300">⭐ Preferito</Badge>}
                        {!t.file_exists && <Badge variant="destructive">File mancante</Badge>}
                      </div>
                      <div className="text-xs text-gray-500">{t.filename} • {Math.round((t.size_bytes || 0) / 1024)} KB</div>
                    </div>
                    <div className="flex items-center gap-1">
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => handleSetPreferred(t.id)}
                        title={preferredId === t.id ? 'Rimuovi preferito' : 'Imposta come preferito (solo per te)'}
                        data-testid={`prefer-${t.id}`}
                      >
                        <Star className={`w-4 h-4 ${preferredId === t.id ? 'text-amber-500 fill-amber-500' : ''}`} />
                      </Button>
                      {canEdit && (
                        <Button size="sm" variant="outline" onClick={() => handleDeleteTemplate(t.id)}>
                          <Trash2 className="w-4 h-4 text-red-500" />
                        </Button>
                      )}
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </TabsContent>
      </Tabs>

      {/* === Record Editor Dialog === */}
      <Dialog open={dialog} onOpenChange={setDialog}>
        <DialogContent className="max-w-6xl max-h-[92vh] overflow-y-auto" data-testid="temperature-form-dialog">
          <DialogHeader>
            <DialogTitle>{editingId ? 'Modifica Scheda Temperature' : 'Nuova Scheda Temperature'}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <HaccpConsultantHeader pdv={pdvList.find(p => p.id === form.pdv_id)} />
            {form.pdv_id && (
              <div className="flex flex-wrap items-center gap-2 p-3 bg-blue-50 border border-blue-200 rounded">
                <span className="text-xs font-semibold text-blue-700 mr-2">🌡️ Punti di rilevazione della UL ({haccpPoints.length}):</span>
                {haccpPoints.length === 0 ? (
                  <Button size="sm" variant="outline" onClick={seedDefaultPoints} data-testid="seed-points-btn">
                    <Plus className="w-4 h-4 mr-1" /> Carica 5 standard (Frigo, Freezer, Vetrina, Cella…)
                  </Button>
                ) : (
                  <>
                    <div className="flex flex-wrap gap-1">
                      {haccpPoints.map(p => (
                        <Badge key={p.id} variant="outline" className="text-xs">
                          {p.name} <span className="ml-1 text-gray-500">({p.expected_min}/{p.expected_max} {p.unit})</span>
                        </Badge>
                      ))}
                    </div>
                    <Button size="sm" variant="default" onClick={autoPopulateFromPoints} data-testid="auto-populate-btn" className="ml-auto">
                      ✨ Genera rilevazioni di oggi
                    </Button>
                  </>
                )}
              </div>
            )}
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
              <div>
                <Label>Unità Locale *</Label>
                <Select value={form.pdv_id} onValueChange={(v) => setForm({ ...form, pdv_id: v })}>
                  <SelectTrigger data-testid="ul-select"><SelectValue placeholder="Seleziona UL" /></SelectTrigger>
                  <SelectContent>
                    {pdvList.map((p) => (
                      <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Titolo (es. Maggio 2026)</Label>
                <Input value={form.titolo} onChange={(e) => setForm({ ...form, titolo: e.target.value })} />
              </div>
              <div>
                <Label>Periodo dal</Label>
                <Input type="date" value={form.periodo_dal} onChange={(e) => setForm({ ...form, periodo_dal: e.target.value })} />
              </div>
              <div>
                <Label>Periodo al</Label>
                <Input type="date" value={form.periodo_al} onChange={(e) => setForm({ ...form, periodo_al: e.target.value })} />
              </div>
              <div className="lg:col-span-3">
                <Label>Punti di rilevazione (separati da ; oppure ,)</Label>
                <Input
                  value={form.punti_rilevazione_csv}
                  onChange={(e) => setForm({ ...form, punti_rilevazione_csv: e.target.value })}
                  placeholder="Frigo banco vetrina; Freezer; Vetrina espositiva"
                />
              </div>
              <div>
                <Label>Template DOCX</Label>
                <Select value={form.template_id || '__default__'} onValueChange={(v) => setForm({ ...form, template_id: v === '__default__' ? '' : v })}>
                  <SelectTrigger><SelectValue placeholder="—" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__default__">Default disponibile</SelectItem>
                    {templates.map((t) => (
                      <SelectItem key={t.id} value={t.id}>{t.name}{t.is_default ? ' (default)' : ''}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="border-t pt-3">
              <div className="flex justify-between items-center mb-2">
                <h3 className="font-semibold">Rilevazioni ({form.letture.length})</h3>
                <Button size="sm" variant="outline" onClick={addReading} data-testid="add-reading-btn">
                  <Plus className="w-4 h-4 mr-1" /> Aggiungi
                </Button>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full text-sm border-collapse">
                  <thead>
                    <tr className="bg-gray-100 text-left">
                      <th className="p-2 w-8">#</th>
                      <th className="p-2 min-w-[120px]">Data</th>
                      <th className="p-2 min-w-[90px]">Ora</th>
                      <th className="p-2 min-w-[160px]">Punto</th>
                      <th className="p-2 min-w-[90px]">T °C</th>
                      <th className="p-2 min-w-[110px]">T attesa</th>
                      <th className="p-2 min-w-[90px]">Conf.</th>
                      <th className="p-2 min-w-[200px]">Azione correttiva</th>
                      <th className="p-2 min-w-[90px]">Op.</th>
                      <th className="p-2 w-8"></th>
                    </tr>
                  </thead>
                  <tbody>
                    {form.letture.map((r, i) => (
                      <tr key={i} className="border-t">
                        <td className="p-1 text-gray-500">{i + 1}</td>
                        <td className="p-1"><Input type="date" value={r.data} onChange={(e) => setReading(i, { data: e.target.value })} /></td>
                        <td className="p-1"><Input type="time" value={r.ora} onChange={(e) => setReading(i, { ora: e.target.value })} /></td>
                        <td className="p-1">
                          {punti.length > 0 ? (
                            <Select value={r.punto_rilevazione || '__custom__'} onValueChange={(v) => setReading(i, { punto_rilevazione: v === '__custom__' ? '' : v })}>
                              <SelectTrigger><SelectValue placeholder="Punto" /></SelectTrigger>
                              <SelectContent>
                                {punti.map((p) => (<SelectItem key={p} value={p}>{p}</SelectItem>))}
                                <SelectItem value="__custom__">— Altro —</SelectItem>
                              </SelectContent>
                            </Select>
                          ) : (
                            <Input value={r.punto_rilevazione} onChange={(e) => setReading(i, { punto_rilevazione: e.target.value })} />
                          )}
                        </td>
                        <td className="p-1"><Input type="number" step="0.1" value={r.temperatura} onChange={(e) => setReading(i, { temperatura: e.target.value })} /></td>
                        <td className="p-1"><Input value={r.temperatura_attesa} onChange={(e) => setReading(i, { temperatura_attesa: e.target.value })} placeholder="0/+4" /></td>
                        <td className="p-1 text-center">
                          <button type="button" onClick={() => setReading(i, { conforme: !r.conforme })} className="p-1">
                            {r.conforme ? <CheckCircle2 className="w-5 h-5 text-emerald-500" /> : <XCircle className="w-5 h-5 text-red-500" />}
                          </button>
                        </td>
                        <td className="p-1"><Input value={r.azione_correttiva} onChange={(e) => setReading(i, { azione_correttiva: e.target.value })} /></td>
                        <td className="p-1"><Input value={r.operatore} onChange={(e) => setReading(i, { operatore: e.target.value })} placeholder="Sigla" /></td>
                        <td className="p-1">
                          <Button size="sm" variant="ghost" onClick={() => removeReading(i)} disabled={form.letture.length <= 1}>
                            <Trash2 className="w-4 h-4 text-red-500" />
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            <div>
              <Label>Note</Label>
              <Textarea rows={2} value={form.note} onChange={(e) => setForm({ ...form, note: e.target.value })} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setDialog(false)}>Annulla</Button>
            <Button onClick={handleSave} disabled={saving} data-testid="save-temperature-btn">
              {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Save className="w-4 h-4 mr-2" />}
              Salva Scheda
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* === Upload Template Dialog === */}
      <Dialog open={uploadDialog} onOpenChange={setUploadDialog}>
        <DialogContent data-testid="upload-template-dialog">
          <DialogHeader>
            <DialogTitle>Carica Template DOCX — Temperature</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label>Nome template *</Label>
              <Input value={tplName} onChange={(e) => setTplName(e.target.value)} placeholder="es. Scheda Temperature Standard" />
            </div>
            <div>
              <Label>File .docx *</Label>
              <Input type="file" accept=".docx" onChange={(e) => setTplFile(e.target.files?.[0] || null)} />
            </div>
            <label className="flex items-center gap-2 text-sm">
              <input type="checkbox" checked={tplDefault} onChange={(e) => setTplDefault(e.target.checked)} />
              Imposta come default per Temperature
            </label>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setUploadDialog(false)}>Annulla</Button>
            <Button onClick={handleUploadTemplate} disabled={uploadingTpl}>
              {uploadingTpl ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Upload className="w-4 h-4 mr-2" />}
              Carica
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
